import { Typography, Tooltip } from '@mui/material';
import LeoIcon from '@mui/icons-material/Language';
import GeoIcon from '@mui/icons-material/Update';
import BaseIcon from '@mui/icons-material/FlashOn';
import TimeshareIcon from '@mui/icons-material/HourglassBottom';
import { GridCellParams } from '@mui/x-data-grid';
import {
  getColor,
  getConnection,
  getMode,
  getActiveSnapshot,
} from '../../../utils/analyzerStatusUtils';
import { ModuleType } from 'features/analyzerModules/types';
import { useAllSnapshotsWithoutCrosspol } from 'features/snapshots/api/getAllSnapshotsWithoutCrosspol';
import { useAnalyzers } from 'features/analyzers/api/getAnalyzers';
import { useAnalyzerModules } from 'features/analyzerModules/api/getAnalyzerModules';

const useSetupTable = (
  analyzerName: string,
  analyzerSerial: string,
  module: null | ModuleType
) => {
  const { data: analyzers } = useAnalyzers({});
  const { data: snapshots } = useAllSnapshotsWithoutCrosspol({});
  const { data: modules } = useAnalyzerModules({});
  const columns = [
    { field: 'id', hide: true },
    {
      field: 'device',
      headerName: 'Device Name',
      width: 180,
      headerClassName: 'system-page--header',
    },
    {
      field: 'serial',
      headerName: 'Serial',
      width: 150,
      headerClassName: 'system-page--header',
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      headerClassName: 'system-page--header',
      renderCell: (params: GridCellParams) => {
        const matchingAnalyzer = analyzers?.find(
          (item) => item?.id === params.row.id
        );
        // getColor and getConnection handle the possibility that matchingAnalyzer is undefined
        return (
          <Typography
            variant='subtitle2'
            style={{ color: getColor(matchingAnalyzer) }}
          >
            {getConnection(matchingAnalyzer)}
          </Typography>
        );
      },
    },
    {
      field: 'mode',
      headerName: 'Mode',
      width: 150,
      headerClassName: 'system-page--header',
    },
    {
      field: 'activeSnapshot',
      headerName: 'Active Snapshot',
      width: 180,
      headerClassName: 'system-page--header',
    },
    {
      field: 'modules',
      headerName: 'Modules',
      headerClassName: 'system-page--header',
      minWidth: 150,
      renderCell: (params: GridCellParams) => {
        const matchingModules = modules?.find(
          (module) => module.analyzer_id === params.row.id
        );
        if (!matchingModules) return null;
        const moduleIcons = matchingModules.types.map((type, idx) => {
          if (!type) return <div />;
          if (type === 'LEO')
            return (
              <Tooltip key={idx} title='LEO' placement='top-start'>
                <LeoIcon style={{ marginRight: '.5rem' }} />
              </Tooltip>
            );
          if (type === 'GEO')
            return (
              <Tooltip key={idx} title='GEO' placement='top-start'>
                <GeoIcon style={{ marginRight: '.5rem' }} />
              </Tooltip>
            );
          if (type === 'BASE')
            return (
              <Tooltip key={idx} title='BASE' placement='top-start'>
                <BaseIcon style={{ marginRight: '.5rem' }} />
              </Tooltip>
            );
          if (type === 'TIMESHARE')
            return (
              <Tooltip key={idx} title='TIMESHARE' placement='top-start'>
                <TimeshareIcon style={{ marginRight: '.5rem' }} />
              </Tooltip>
            );
          if (type === 'LITE')
            return (
              <Tooltip key={idx} title='LITE' placement='top-start'>
                <BaseIcon style={{ marginRight: '.5rem', color: '#007FFF' }} />
              </Tooltip>
            );
          return <div />;
        });
        return <>{moduleIcons.map((icon) => icon)}</>;
      },
    },
  ];
  const rows = analyzers
    ?.filter((item) => {
      if (
        analyzerName &&
        !item.name.toLowerCase().includes(analyzerName.toLowerCase())
      )
        return false;
      if (
        analyzerSerial &&
        !item.serial.toLowerCase().includes(analyzerSerial.toLowerCase())
      )
        return false;
      if (module) {
        // Check whether any of the analyzer's associated modules matches the selected module filter type
        const analyzerModules = modules?.find(
          (module) => module.analyzer_id === item.id
        );
        if (!analyzerModules) return false;
        return analyzerModules.types.some(
          (moduleType) => moduleType === module
        );
      }
      return true;
    })
    .map((item) => ({
      ...item,
      device: item.name,
      status: getConnection(item),
      mode: getMode(item),
      activeSnapshot: getActiveSnapshot(item, snapshots),
    }));
  return { rows, columns };
};

export default useSetupTable;
